import useSWR from "swr";
import type { NskResponse, SwrReturnType } from "~/models/Api";
import type { Survey, SurveySetting } from "~/models/Survey";
import {
	deleteRequest,
	postRequest,
	putRequest
} from "~/utils/api/api_handler";

const paths = {
	activate: "/vendors/surveys/:id/activate",
	changeEnabled: "/vendors/surveys/:id/change_enabled",
	create: "/vendors/surveys",
	delete: "/vendors/surveys/:id",
	index: "/vendors/surveys",
	show: "/vendors/surveys/:id",
	update: "/vendors/surveys/:id",
	updateSeq: "/vendors/surveys/update_seq"
};

type TIndex = () => SwrReturnType<Survey[]>;
type TShow = (id: number | null) => SwrReturnType<Survey>;
type ReturnType = {
	index: TIndex;
	show: TShow;
};

export const useSurveySettings = (): ReturnType => {
	const index: TIndex = () => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data, mutate, isLoading, error } = useSWR<Survey[]>(paths.index);
		return {
			data: data ?? null,
			error,
			isLoading,
			mutate
		};
	};
	const show: TShow = (id: number | null) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data, mutate, isLoading, error } = useSWR<Survey>(
			id === null ? null : paths.show.replace(":id", String(id))
		);
		return {
			data: data ?? null,
			error,
			isLoading,
			mutate
		};
	};
	return { index, show };
};

export const create = async (survey: SurveySetting): Promise<NskResponse> =>
	postRequest(paths.create, {
		survey
	});

export const update = async (
	survey: SurveySetting,
	surveyId: number
): Promise<NskResponse> =>
	putRequest(paths.update.replace(":id", String(surveyId)), {
		survey
	});

export const destroy = async (surveyId: number): Promise<NskResponse> =>
	deleteRequest(paths.delete.replace(":id", String(surveyId)));

export const activate = async (surveyId: number): Promise<NskResponse> =>
	putRequest(paths.activate.replace(":id", String(surveyId)));

export const changeEnabled = async (
	surveyId: number,
	enabled: boolean
): Promise<NskResponse> =>
	putRequest(paths.changeEnabled.replace(":id", String(surveyId)), { enabled });

export const updateSeq = async (surveys: Survey[]): Promise<NskResponse> =>
	postRequest(paths.updateSeq, {
		surveys
	});
